/* eslint-disable no-restricted-syntax -- this line was auto generated, hence fix the issue timely */
import React, { FC, useMemo } from "react";
import { HiddenVisually, Text, Title } from "@bookingcom/bui-react";
import { I18nChildContext, useI18n } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { SPACER_PADDED } from "@flights/constants";
import { UIFlightSegmentTimelined, UITimelineRow } from "@flights/types";
import { TimelineLeg } from "./TimelineLeg";
import { TimelineStop } from "./TimelineStop";
import Frame from "../../Frame";
import styles from "./TimelineSegment.module.css";
import useSearchCabinClass from "../../../../hooks/useSearchCabinClass";
import { AmenityProps } from "./TimelineLegInfo";
import { formattingUtils } from "@flights/web-api-utils-universal";
import useFlightsFunnelWebCondensedItinerary from "../../../../utils/experiments/funnel/flights_funnel_web_condensed_itinerary";
import useProfiler from "../../../../hooks/useProfiler";
import useRouteName from "../../../../hooks/useRouteName";
import flights_web_mdot_cnp_condensed, {
  useFlightsWebMdotCnpCondensed
} from "../../../../utils/experiments/funnel/flights_web_mdot_cnp_condensed";

type Props = {
  segment: UIFlightSegmentTimelined;
  segmentIndex: number;
  hideHeading?: boolean;
  hideCabinClassMismatch?: boolean;
} & AmenityProps; // flights_web_route_happy_amenities_ddot, flights_web_route_happy_amenities_mdot

export const TimelineSegment: FC<Props> = ({
  segment,
  segmentIndex,
  showAmenities = false,
  hideCabinClassMismatch,
  amenitiesVariant,
  hideHeading = false,
  onAmenitiesPopoverOpen
}) => {
  const { isPb, isMobile } = useProfiler();
  const flights_funnel_web_condensed_itinerary = useFlightsFunnelWebCondensedItinerary(); // eslint-disable-line @typescript-eslint/naming-convention
  const isCondencedItinerary = !isPb() && isMobile() && flights_funnel_web_condensed_itinerary();

  const routeName = useRouteName();
  useFlightsWebMdotCnpCondensed(false).trackStages();
  const isMdotCnpCondensed = useMemo(() => {
    return routeName === "checkout-payment" && isMobile() && !!flights_web_mdot_cnp_condensed.trackWithDefaultStage();
  }, [routeName, isMobile]);

  return (
    <Frame
      className={isMdotCnpCondensed ? "" : isCondencedItinerary ? styles.rootSmall : styles.root}
      data-testid={`timeline_segment_${segmentIndex}`}
    >
      {!hideHeading ? <Header segment={segment} hideCabinClassMismatch={hideCabinClassMismatch} /> : undefined}
      <div
        className={[styles.legsWrapper, (isCondencedItinerary || isMdotCnpCondensed) && styles.condensedItinerary].join(
          ""
        )}
      >
        {segment.timeline.map((item, index) => (
          <Item
            data={item}
            isLastOne={index === segment.timeline.length - 1}
            key={index}
            prevTimeLineData={segment.timeline[index - 1]}
            nextTimeLineData={segment.timeline[index + 1]}
            idx={index}
            showAmenities={showAmenities}
            amenitiesVariant={amenitiesVariant}
            onAmenitiesPopoverOpen={onAmenitiesPopoverOpen}
          />
        ))}
      </div>
    </Frame>
  );
};

const Header: FC<{
  segment: UIFlightSegmentTimelined;
  hideCabinClassMismatch?: boolean;
}> = ({ segment, hideCabinClassMismatch = false }) => {
  const i18n = useI18n() as I18nChildContext;
  const { arrivalAirport, stops, durationMinutes } = segment;
  const { searchCabinClass } = useSearchCabinClass();

  const isCabinClassMismatch = useMemo(() => {
    if (hideCabinClassMismatch) {
      return false;
    }

    return segment.legs.some((l) => l.cabinClass !== searchCabinClass);
  }, [segment, searchCabinClass, hideCabinClassMismatch]);

  const stopsText = useMemo(() => {
    if (stops === 0) {
      return i18n.trans(t("flights_filter_stops_none"));
    } else {
      return i18n.trans(t("flights_route_num_stops", { num_exception: stops, variables: { num_stops: stops } }));
    }
  }, [i18n, stops]);

  const durationText = useMemo(() => {
    return formattingUtils.flightDuration(durationMinutes, i18n);
  }, [i18n, durationMinutes]);

  const durationTextAria = useMemo(() => {
    return formattingUtils.flightDurationAria(durationMinutes, i18n);
  }, [i18n, durationMinutes]);

  const { isPb, isMobile } = useProfiler();
  const flights_funnel_web_condensed_itinerary = useFlightsFunnelWebCondensedItinerary(); // eslint-disable-line @typescript-eslint/naming-convention
  const isCondencedItinerary = !isPb() && isMobile() && flights_funnel_web_condensed_itinerary();

  return (
    <Frame mb={isCondencedItinerary ? 2 : 4}>
      <Title
        variant="strong_1"
        attributes={{ "data-testid": "timeline_segment_title" }}
        title={i18n.trans(
          t("flights_details_route", {
            variables: {
              city_name: arrivalAirport.cityName || arrivalAirport.name
            }
          })
        )}
        titleTagName="h2"
      />
      <Text
        variant="body_2"
        className={styles.summary}
        attributes={{
          "data-testid": "timeline_segment_flight_duration",
          "aria-hidden": "true"
        }}
      >
        {stopsText}
        {SPACER_PADDED}
        {durationText}
        {isCabinClassMismatch ? ` ${i18n.trans(t("flights_fd_cabin_mismatch"))}` : null}
      </Text>
      <HiddenVisually>{`${stopsText} ${durationTextAria} ${
        isCabinClassMismatch ? i18n.trans(t("flights_fd_cabin_mismatch")) : ""
      }`}</HiddenVisually>
    </Frame>
  );
};

const Item: FC<
  {
    data: UITimelineRow;
    isLastOne?: boolean;
    prevTimeLineData: UITimelineRow;
    nextTimeLineData: UITimelineRow;
    idx: number;
  } & AmenityProps
> = ({
  data,
  isLastOne,
  prevTimeLineData,
  nextTimeLineData,
  idx,
  showAmenities = false,
  amenitiesVariant,
  onAmenitiesPopoverOpen
}) => {
  let view: React.ReactElement | null = null;

  if (data.type === "FLIGHT") {
    //  `shouldHideAnythingBeneath={true}` on last leg visually covers leftovers of
    //  `legsWrapper`'s dashed connection line at the bottom of segment
    view = (
      <TimelineLeg
        leg={data}
        shouldHideAnythingBeneath={isLastOne}
        idx={idx}
        showAmenities={showAmenities}
        amenitiesVariant={amenitiesVariant}
        onAmenitiesPopoverOpen={onAmenitiesPopoverOpen}
      />
    );
  } else if (data.type === "STOP") {
    view = <TimelineStop stop={data} prevLegData={prevTimeLineData} nextLegData={nextTimeLineData} />;
  }

  return view;
};
